<script setup lang="ts">
  import { computed, onMounted, provide, readonly, ref } from 'vue';
  import { useResizeObserver } from '@vueuse/core';
  import { CANVAS_PAGE_KEY, ICanvasPageSize } from './canvas-page';

  /**
   * The canvas page archetype (V2 guidelines, Layout; plan §5.2): the root is
   * the page's only scroller, with a sticky head and an optional sticky rail
   * on the canvas, white content cards, and an optional editor docked at the
   * right (or a bottom sheet mounted in the zero-size aside). Tiers come from
   * the measured width (emitted as `resize`), never from the window height.
   */
  const props = withDefaults(
    defineProps<{
      /** The centred content width; null = full width. */
      maxWidth?: number | null;
      /** null = no rail column. */
      railWidth?: number | null;
      asideWidth?: number | null;
      /** The aside is a docked column (else a zero-size host for a sheet). */
      asideDocked?: boolean;
      asideOpen?: boolean;
      gutter?: number;
      bottomPad?: number;
      /** A white head with a hairline (phones) instead of the canvas. */
      headSurface?: boolean;
    }>(),
    {
      maxWidth: null,
      railWidth: null,
      asideWidth: null,
      asideDocked: false,
      asideOpen: false,
      gutter: 24,
      bottomPad: 24,
      headSurface: false,
    }
  );

  const emit = defineEmits<{ (e: 'resize', size: ICanvasPageSize): void }>();

  const root = ref<HTMLElement | null>(null);
  const head = ref<HTMLElement | null>(null);
  const width = ref(0);
  const viewHeight = ref(0);
  const headHeight = ref(0);

  function measure() {
    if (!root.value || !head.value) return;
    const next = {
      // offsetWidth: a scrollbar appearing with the content must not flip
      // the tier near a breakpoint (and the keyboard never changes it).
      width: root.value.offsetWidth,
      viewHeight: root.value.clientHeight,
      headHeight: head.value.offsetHeight,
    };
    if (
      next.width === width.value &&
      next.viewHeight === viewHeight.value &&
      next.headHeight === headHeight.value
    )
      return;
    width.value = next.width;
    viewHeight.value = next.viewHeight;
    headHeight.value = next.headHeight;
    emit('resize', next);
  }

  // Synchronous first read: the list sizes its first render on it.
  onMounted(measure);
  useResizeObserver(root, measure);
  useResizeObserver(head, measure);

  function scrollToElement(element: HTMLElement, extra = 8) {
    const scroller = root.value;
    if (!scroller) return;
    scroller.scrollTop +=
      element.getBoundingClientRect().top -
      scroller.getBoundingClientRect().top -
      headHeight.value -
      extra;
  }

  provide(CANVAS_PAGE_KEY, {
    width: readonly(width),
    headHeight: readonly(headHeight),
    viewHeight: readonly(viewHeight),
    scroller: readonly(root),
    scrollToElement,
  });

  const rootStyle = computed(() => ({
    paddingLeft: `${props.gutter}px`,
    paddingRight: `${props.gutter}px`,
    paddingBottom: `${props.bottomPad}px`,
    '--cp-gutter': `${props.gutter}px`,
    '--cp-head-h': `${headHeight.value}px`,
    '--cp-view-h': `${viewHeight.value}px`,
  }));
  const innerStyle = computed(() =>
    props.maxWidth ? { maxWidth: `${props.maxWidth}px` } : undefined
  );
  const asideStyle = computed(() =>
    props.asideDocked && props.asideWidth
      ? { width: `${props.asideWidth}px` }
      : undefined
  );

  // ICanvasPageHandle: the page that renders the canvas scrolls and
  // observes through it (provide only reaches descendants).
  defineExpose({ scroller: root, scrollToElement });
</script>

<template>
  <div ref="root" class="v2-canvas-page" :style="rootStyle">
    <div class="cp-inner" :style="innerStyle">
      <div ref="head" class="cp-head" :class="{ 'is-surface': headSurface }">
        <slot name="head" />
      </div>
      <!-- No element between the scroller and the sticky rail/aside may get
           overflow: it would become their scroll container and unstick them. -->
      <div class="cp-body">
        <div
          v-if="railWidth"
          class="cp-rail"
          :style="{ width: `${railWidth}px` }"
        >
          <slot name="rail" />
        </div>
        <div class="cp-main"><slot /></div>
        <!-- Never display:none while not docked: a fixed sheet inside it
             would disappear. -->
        <div
          v-show="!asideDocked || asideOpen"
          class="cp-aside"
          :class="asideDocked ? 'is-docked' : 'is-host'"
          :style="asideStyle"
        >
          <slot name="aside" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-canvas-page {
    @apply tw-min-h-0 tw-overflow-x-hidden tw-overflow-y-auto tw-bg-canvas;
    flex: 1 1 0;
    overscroll-behavior-y: contain;

    .cp-inner {
      @apply tw-mx-auto;
    }

    .cp-head {
      @apply tw-sticky tw-top-0 tw-bg-canvas tw-pt-3;
      z-index: 20;
      margin: 0 calc(-1 * var(--cp-gutter));
      padding-left: var(--cp-gutter);
      padding-right: var(--cp-gutter);

      &.is-surface {
        @apply tw-bg-surface;
        box-shadow: inset 0 -1px 0 rgb(var(--c-neutral-200));

        // The content starts on the canvas below the hairline, never flush
        // against the white head (the same gap as between cards).
        & + .cp-body {
          @apply tw-pt-3;
        }
      }
    }

    .cp-body {
      @apply tw-flex tw-items-start;
    }

    .cp-rail {
      @apply tw-sticky tw-mr-4 tw-flex-none tw-overflow-y-auto;
      top: var(--cp-head-h);
      max-height: calc(var(--cp-view-h) - var(--cp-head-h) - 16px);
    }

    .cp-main {
      @apply tw-min-w-0;
      flex: 1 1 auto;
    }

    .cp-aside {
      @apply tw-flex-none;

      &.is-docked {
        @apply tw-sticky tw-ml-4;
        top: var(--cp-head-h);
        height: calc(var(--cp-view-h) - var(--cp-head-h) - 16px);
      }

      &.is-host {
        @apply tw-h-0 tw-w-0 tw-overflow-visible;
      }
    }
  }
</style>
